import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dealEventLogControllerGetDealEventSubjectsOptions } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { MAX_RECIPIENT_OPTIONS } from '@dealstack/shared/lib/common/business/dealEvent';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue';
import type { AsyncFilterMultiSelectProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { dealEventLogRequestOptions } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventLogUnread.ts';
import type { DealEventFilterOption } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogFilterOptions.utils.ts';

export const useDealEventSubjects = (dealId: string, enabled: boolean) => {
	const [opened, setOpened] = useState(false);
	const [search, setSearch] = useState('');
	const debouncedSearch = useDebounceValue(search);

	const query = useQuery({
		...dealEventLogControllerGetDealEventSubjectsOptions({
			...dealEventLogRequestOptions(dealId),
			query: { limit: MAX_RECIPIENT_OPTIONS, search: debouncedSearch || undefined },
		}),
		enabled: enabled && opened && !!dealId,
		staleTime: 0,
	});

	const options = useMemo<DealEventFilterOption[]>(
		() =>
			(query.data?.subjects ?? []).map(({ id, name, email }) => ({
				value: id,
				label: name ?? email,
			})),
		[query.data]
	);

	const async: AsyncFilterMultiSelectProps = {
		serverSide: true,
		onDropdownOpen: () => setOpened(true),
		onSearchChange: setSearch,
		isLoading: query.isFetching,
		hasMore: !!query.data?.hasMore,
	};

	return { options, async };
};
